import { RefreshApi } from '@repo/api-gw-sdk';
import useSWR, { type SWRConfiguration } from 'swr';

import { createConfig } from '@/contexts/api-config';
import type { Environment } from '@/contexts/environment';
import { shouldRefreshToken } from '@/libs/auth';

const createFetchOptions = (authToken?: string, options?: RequestInit) => ({
  ...options,
  headers: {
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    ...options?.headers,
  },
});

export const refreshTokenIfNeeded = async (
  authToken: string,
  baseUrl: string,
  updateAuthToken: (token: string) => void
): Promise<string> => {
  if (shouldRefreshToken(authToken)) {
    const { message: newAuthToken } = await new RefreshApi(
      createConfig(baseUrl, '')
    ).refresh();
    if (newAuthToken) {
      updateAuthToken(newAuthToken);
      return newAuthToken;
    }
  }

  return authToken;
};

const fetchFactory = (
  authToken: string,
  updateAuthToken: (token: string) => void,
  environment: Environment,
  useRefreshToken: boolean
) => {
  // The live token for this client instance. Updated in place when a refresh
  // resolves so subsequent requests evaluate expiry against the fresh token
  // rather than the one captured when the factory was created — otherwise every
  // request after the first keeps re-refreshing until React re-renders.
  let currentToken = authToken;
  // Non-null only while a refresh is in flight, so concurrent requests share the
  // same refresh instead of each firing their own.
  let refreshPromise: Promise<string> | null = null;

  const refreshToken = () => {
    if (!useRefreshToken) {
      return Promise.resolve(currentToken);
    }

    if (refreshPromise) {
      return refreshPromise;
    }

    if (!shouldRefreshToken(currentToken)) {
      return Promise.resolve(currentToken);
    }

    refreshPromise = refreshTokenIfNeeded(
      currentToken,
      environment.clientApiBaseUrl,
      updateAuthToken
    ).then(
      (token) => {
        currentToken = token;
        refreshPromise = null;
        return token;
      },
      () => {
        refreshPromise = null;
        return currentToken;
      }
    );
    return refreshPromise;
  };

  return async (url: string, options?: RequestInit) => {
    const newAuthToken = await refreshToken();
    const res = await fetch(url, createFetchOptions(newAuthToken, options));

    if (useRefreshToken && res.status === 401) {
      location.href = '/logout';
    }

    if (res.status === 204) {
      // No content
      return;
    }

    return res.json();
  };
};

export type HttpClient = ReturnType<typeof createHttpClient>;

const swrDefaultConfig: SWRConfiguration = {
  keepPreviousData: true,
  revalidateOnFocus: false,
  onError(err) {
    window.reportError(err);
  },
  onErrorRetry: (error, key, config, revalidate, opts) => {
    // Don't retry on 403 (Forbidden) - permission won't change on retry
    // Check both 'code' (ApiException) and 'status' (standard fetch errors) for defensiveness
    const errorCode = error?.code ?? error?.status;
    if (errorCode === 403) {
      return;
    }

    // Replicate SWR's default exponential backoff with jitter
    const maxRetryCount = config.errorRetryCount;
    const currentRetryCount = opts.retryCount;

    // Exponential backoff: random jitter (0.5-1.5) * 2^retryCount * errorRetryInterval
    const timeout =
      ~~(
        (Math.random() + 0.5) *
        (1 << (currentRetryCount < 8 ? currentRetryCount : 8))
      ) * config.errorRetryInterval;

    if (maxRetryCount !== undefined && currentRetryCount > maxRetryCount) {
      return;
    }

    setTimeout(() => {
      void revalidate(opts);
    }, timeout);
  },
};

export const createHttpClient = (
  authToken: string,
  updateAuthToken: (token: string) => void,
  environment: Environment,
  useRefreshToken: boolean
) => {
  const fetcher = fetchFactory(
    authToken,
    updateAuthToken,
    environment,
    useRefreshToken
  );

  return {
    get: (url: string | null, swrOptions?: SWRConfiguration) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data, ...rest } = useSWR(
        url ? environment.clientApiBaseUrl + url : null,
        fetcher,
        {
          ...swrDefaultConfig,
          ...swrOptions,
        }
      );
      return {
        ...rest,
        body: data,
      };
    },
    fetch: (url: string) => {
      return fetcher(environment.clientApiBaseUrl + url, {
        method: 'GET',
      });
    },
    post: (url: string, body: unknown) => {
      return fetcher(environment.clientApiBaseUrl + url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    put: (url: string, body: unknown) => {
      return fetcher(environment.clientApiBaseUrl + url, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },
    patch: (url: string, body: unknown) => {
      return fetcher(environment.clientApiBaseUrl + url, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    delete: (url: string) => {
      return fetcher(environment.clientApiBaseUrl + url, {
        method: 'DELETE',
      });
    },
    request: (
      method: string,
      url: string,
      options?: { body?: string; headers?: Record<string, string> }
    ) => {
      return fetcher(environment.clientApiBaseUrl + url, {
        method,
        ...(options?.body !== undefined ? { body: options.body } : {}),
        ...(options?.headers ? { headers: options.headers } : {}),
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    executeWithConfig: <T extends (...args: any[]) => any>(
      fn: T,
      baseUrl: string | null,
      urlPath: string,
      swrOptions: SWRConfiguration = {},
      ...args: Parameters<T>
    ) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useSWR<Awaited<ReturnType<T>>>(
        baseUrl === null ? null : [baseUrl, urlPath, ...args],
        () => fn(...args),
        {
          ...swrDefaultConfig,
          ...swrOptions,
        }
      );
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    execute: <T extends (...args: any[]) => any>(
      fn: T,
      baseUrl: string | null,
      urlPath: string,
      ...args: Parameters<T>
    ) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useSWR<Awaited<ReturnType<T>>>(
        baseUrl === null ? null : [baseUrl, urlPath, ...args],
        () => fn(...args),
        swrDefaultConfig
      );
    },
  };
};
